import { CheckCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useGroupStore from '../../store/group';
import useUserStore from '../../store/user';

export default function InvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const { acceptInvitation } = useGroupStore();
  const {
    user,
    isInitialized,
    checkSession,
    pendingInvitationCode,
    setPendingInvitationCode,
    clearPendingInvitationCode,
  } = useUserStore(state => state);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (!code) {
      setError(t('groups.invitation.invalidCode'));
      return;
    }

    if (isInitialized) {
      if (user) {
        // User is logged in, ready to accept
        setIsReady(true);
        // Clear stored code if we are on the same invitation route
        if (pendingInvitationCode === code) {
          clearPendingInvitationCode();
        }
      } else {
        // Remember code and go to Home (login)
        setPendingInvitationCode(code);
      }
    }
  }, [
    code,
    t,
    user,
    isInitialized,
    pendingInvitationCode,
    setPendingInvitationCode,
    clearPendingInvitationCode,
  ]);

  // If unauthenticated, redirect to home after storing the invitation code
  if (isInitialized && !user) {
    return <Navigate to="/" replace />;
  }

  const handleAccept = async () => {
    if (!code || !isReady) return;

    setIsProcessing(true);
    setError(null);

    try {
      await acceptInvitation(code);
      setSuccess(true);
      setTimeout(() => {
        checkSession();
        navigate('/');
      }, 2000);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to accept invitation';

      if (errorMessage.includes('already belong')) {
        setError(t('groups.invitation.alreadyInGroup'));
      } else if (errorMessage.includes('Invalid or expired')) {
        setError(t('groups.invitation.invalidCode'));
      } else if (errorMessage.includes('different email')) {
        setError(t('groups.invitation.differentEmail'));
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-vh-100 bg-light d-flex align-items-center">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow">
              <Card.Body className="p-5 text-center">
                {success ? (
                  <>
                    <CheckCircle size={64} className="text-success mb-4" />
                    <h3 className="mb-3">{t('groups.invitation.success')}</h3>
                    <p className="text-muted">Redirecting...</p>
                  </>
                ) : (
                  <>
                    <h3 className="mb-4">{t('groups.invitation.title')}</h3>

                    {error ? (
                      <Alert variant="danger">{error}</Alert>
                    ) : (
                      <>
                        <p className="mb-4">
                          {t('groups.invitation.youveBeenInvited')}
                        </p>
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={handleAccept}
                          disabled={isProcessing || !code || !isReady}
                          className="w-100"
                        >
                          {isProcessing ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              {t('groups.invitation.accepting')}
                            </>
                          ) : (
                            t('groups.invitation.accept')
                          )}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
